import {type Node} from '@xyflow/react';
import {type GenType, type ScreenSize} from '@features/flow/flowService';
import {getEffectiveGenType} from '@features/server/utils/getEffectiveGenType';
import {
  COMPONENT_NODE_OFFSET,
  ESTIMATED_COMPONENT_SIZE,
  NODE_SPACING,
  getNodeDimensions,
} from './nodeDimensions';

type CanvasScreen = {
  id: string;
  x: number | null;
  y: number | null;
  screenSize: ScreenSize | null;
  hasActiveStructure: string | null;
  type: 'screen' | 'component';
};

type CanvasLayoutInput = {
  screens: CanvasScreen[];
  streamingGenTypes: Partial<Record<string, GenType>>;
  flowGenType: GenType | null;
  flowId: string;
  selectedScreenIds: string[];
  componentDims?: Map<string, {width: number; height: number}>;
  // When provided, nodes whose scalar inputs are unchanged keep their
  // previous referential identity. This prevents xyflow's per-node store
  // subscriptions from firing for unrelated nodes when one node's data
  // changes (e.g. a single selection toggle).
  nodeCache?: LayoutNodeCache;
};

type CanvasNodeData = {
  chatId: string;
  flowId: string;
  index: number;
  screenSize: ScreenSize | null;
  hasActiveStructure: boolean;
};

export type FrameNodeType = Node<CanvasNodeData, 'frame'>;

type LayoutCacheEntry = {
  node: Node;
  type: 'screen' | 'component';
  positionX: number;
  positionY: number;
  index: number;
  screenSize: ScreenSize | null;
  hasActiveStructure: boolean;
  selected: boolean;
  unpositioned: boolean;
  effectiveGenType: GenType | ScreenSize;
  width: number;
  height: number;
};

export type LayoutNodeCache = Map<string, LayoutCacheEntry>;

function frameToNode(
  flowId: string,
  chatId: string,
  index: number,
  genType: ScreenSize | GenType,
  screenSize: ScreenSize | null,
  hasActiveStructure: boolean,
  x?: number | null,
  y?: number | null,
): Node<CanvasNodeData, 'frame'> {
  const {width, height, offset} = getNodeDimensions(genType);

  let sizeProps;
  if (x === null || x === undefined) {
    sizeProps = {
      initialWidth: width,
      initialHeight: height,
    };
  } else {
    sizeProps = {
      measured: {
        width,
      },
    };
  }

  return {
    id: chatId,
    type: 'frame',
    position: {
      x: x ?? offset * index,
      y: y ?? 0,
    },
    ...sizeProps,
    style: {
      width,
    },
    data: {
      chatId,
      flowId,
      index,
      screenSize,
      hasActiveStructure,
    },
  };
}

export function computeCanvasLayout(input: CanvasLayoutInput): Node[] {
  const {
    screens,
    streamingGenTypes,
    flowGenType,
    flowId,
    selectedScreenIds,
    componentDims,
    nodeCache,
  } = input;
  const selectedSet =
    selectedScreenIds.length === 0 ? null : new Set(selectedScreenIds);

  const screenGenTypes = new Map<string, GenType>();
  for (const screen of screens) {
    if (screen.type === 'component') continue;
    const streamingGenType = streamingGenTypes[screen.id];
    screenGenTypes.set(
      screen.id,
      getEffectiveGenType(
        streamingGenType ? undefined : screen.screenSize,
        streamingGenType,
        flowGenType,
      ),
    );
  }

  let rightmostScreenEdge = 0;
  let screenFallbackX = 0;
  for (const screen of screens) {
    if (screen.type === 'component') continue;
    const genType = screenGenTypes.get(screen.id) ?? 'web-html-css';
    const {width, offset} = getNodeDimensions(genType);
    if (screen.x !== null) {
      rightmostScreenEdge = Math.max(rightmostScreenEdge, screen.x + width);
    } else {
      rightmostScreenEdge = Math.max(
        rightmostScreenEdge,
        screenFallbackX + width,
      );
      screenFallbackX += offset;
    }
  }

  const componentColumnX = rightmostScreenEdge + COMPONENT_NODE_OFFSET;

  let screenFallbackIndex = 0;
  let componentIndex = 0;
  let nextComponentFallbackY = 0;
  let screenOverflowY = 0;
  const seenIds = new Set<string>();

  const nodes = screens.map((screen) => {
    seenIds.add(screen.id);
    const hasStructure = screen.hasActiveStructure !== null;

    if (screen.type === 'component') {
      const currentComponentIndex = componentIndex++;
      const dims = componentDims?.get(screen.id);
      const compHeight = dims?.height ?? ESTIMATED_COMPONENT_SIZE;
      const compWidth = dims?.width ?? ESTIMATED_COMPONENT_SIZE;
      const inCurrentColumn =
        screen.x === null ||
        Math.round(screen.x) === Math.round(componentColumnX);
      const positionY = screen.y ?? nextComponentFallbackY;
      if (inCurrentColumn) {
        nextComponentFallbackY =
          (screen.y ?? nextComponentFallbackY) + compHeight + NODE_SPACING;
      }
      const positionX = screen.x ?? componentColumnX;
      const isSelected = selectedSet ? selectedSet.has(screen.id) : false;
      const unpositioned = screen.x === null;

      const cached = nodeCache?.get(screen.id);
      if (
        cached &&
        cached.type === 'component' &&
        cached.positionX === positionX &&
        cached.positionY === positionY &&
        cached.index === currentComponentIndex &&
        cached.screenSize === screen.screenSize &&
        cached.hasActiveStructure === hasStructure &&
        cached.selected === isSelected &&
        cached.unpositioned === unpositioned &&
        cached.width === compWidth &&
        cached.height === compHeight
      ) {
        return cached.node;
      }

      // Unpositioned components use initialWidth/initialHeight so ReactFlow
      // measures their actual content size (enabling accurate reflow positioning).
      // Positioned components use `measured` to tell ReactFlow the size without
      // constraining the DOM — flexible sizing with no flicker (see commit f50a6521).
      const dimensionProps = unpositioned
        ? {
            initialWidth: ESTIMATED_COMPONENT_SIZE,
            initialHeight: ESTIMATED_COMPONENT_SIZE,
          }
        : {
            measured: {
              width: compWidth,
              height: compHeight,
            },
          };

      const node: Node = {
        id: screen.id,
        type: 'component',
        position: {x: positionX, y: positionY},
        ...dimensionProps,
        data: {
          chatId: screen.id,
          flowId,
          index: currentComponentIndex,
          screenSize: screen.screenSize,
          hasActiveStructure: hasStructure,
        },
        selected: isSelected,
      } satisfies Node;

      nodeCache?.set(screen.id, {
        node,
        type: 'component',
        positionX,
        positionY,
        index: currentComponentIndex,
        screenSize: screen.screenSize,
        hasActiveStructure: hasStructure,
        selected: isSelected,
        unpositioned,
        effectiveGenType: 'components',
        width: compWidth,
        height: compHeight,
      });

      return node;
    }

    const effectiveGenType = screenGenTypes.get(screen.id) ?? 'web-html-css';
    const currentIndex = screenFallbackIndex++;
    let screenX = screen.x;
    let screenY = screen.y;
    if (screenX === null) {
      const {width, offset, height} = getNodeDimensions(effectiveGenType);
      const fallbackX = offset * currentIndex;
      if (fallbackX + width > componentColumnX) {
        screenX = 0;
        screenY = screenOverflowY + height + NODE_SPACING;
        screenOverflowY = screenY;
      } else {
        screenX = fallbackX;
      }
    }
    const positionY = screenY ?? 0;
    const isSelected = selectedSet ? selectedSet.has(screen.id) : false;
    const unpositioned = screen.x === null;
    const {width: frameWidth, height: frameHeight} =
      getNodeDimensions(effectiveGenType);

    const cached = nodeCache?.get(screen.id);
    if (
      cached &&
      cached.type === 'screen' &&
      cached.positionX === screenX &&
      cached.positionY === positionY &&
      cached.index === currentIndex &&
      cached.screenSize === screen.screenSize &&
      cached.hasActiveStructure === hasStructure &&
      cached.selected === isSelected &&
      cached.unpositioned === unpositioned &&
      cached.effectiveGenType === effectiveGenType
    ) {
      return cached.node;
    }

    const node: Node = {
      ...frameToNode(
        flowId,
        screen.id,
        currentIndex,
        effectiveGenType,
        screen.screenSize,
        hasStructure,
        screenX,
        screenY,
      ),
      selected: isSelected,
    };

    nodeCache?.set(screen.id, {
      node,
      type: 'screen',
      positionX: screenX,
      positionY,
      index: currentIndex,
      screenSize: screen.screenSize,
      hasActiveStructure: hasStructure,
      selected: isSelected,
      unpositioned,
      effectiveGenType,
      width: frameWidth,
      height: frameHeight,
    });

    return node;
  });

  if (nodeCache) {
    for (const id of nodeCache.keys()) {
      if (!seenIds.has(id)) {
        // eslint-disable-next-line drizzle/enforce-delete-with-where -- This is a Map, not a drizzle query
        nodeCache.delete(id);
      }
    }
  }

  return nodes;
}
